/**
 * Horizon clamp for surface scan tags. On a spherical planet a far settlement's
 * true position sits BELOW the player's local horizon, so its projected tag
 * lands under the player. horizonAnchor keeps clearly-visible objective
 * targets where they are and pins everything else to the planet's geometric
 * tangent horizon at the correct compass bearing. Place labels may force that
 * projection even when their true point is elevated: a place name describes a
 * bearing, not a visible object.
 */
import * as THREE from "three";

const upV = new THREE.Vector3();
const dV = new THREE.Vector3();
const hV = new THREE.Vector3();

/**
 * Split target−eye into horizontal/vertical parts in the eye's local tangent
 * frame (up = away from the planet centre) and clamp the vertical part to no
 * lower than the tangent horizon. For a spherical surface that elevation is
 * `-acos(surfaceRadius / eyeRadius)`: exactly the ray that grazes the planet.
 * Targets already above the horizon return bit-identical unless `force` is
 * true. `out` may alias `target`;
 * pass the CAMERA's world position as `eye` — the camera is what projects the
 * tag, and a feet-level eye reads metres low from a third-person boom.
 */
export function horizonAnchor(
  eye: THREE.Vector3,
  planetCenter: THREE.Vector3,
  target: THREE.Vector3,
  surfaceRadius: number,
  force: boolean,
  out: THREE.Vector3,
): THREE.Vector3 {
  upV.copy(eye).sub(planetCenter);
  const eyeRadius = upV.length();
  if (eyeRadius < 1e-9) return out.copy(target);
  upV.divideScalar(eyeRadius);
  dV.copy(target).sub(eye);
  const u = dV.dot(upV);
  hV.copy(dV).addScaledVector(upV, -u);
  const ratio = Math.max(0, Math.min(1, surfaceRadius / eyeRadius));
  const horizonElevation = -Math.acos(ratio);
  const floor = hV.length() * Math.tan(horizonElevation);
  if (!force && u >= floor) return out.copy(target);
  return out.copy(eye).add(hV).addScaledVector(upV, floor);
}
